// Goal planning math — deterministic heuristics (not investment advice).

export const monthsUntil = (targetDate) => {
  if (!targetDate) return 0;
  const now = new Date();
  const target = new Date(targetDate);
  const months =
    (target.getFullYear() - now.getFullYear()) * 12 +
    (target.getMonth() - now.getMonth());
  return Math.max(0, months);
};

export const yearsUntil = (targetDate) => Math.round((monthsUntil(targetDate) / 12) * 10) / 10;

// Future value of a starting balance plus fixed monthly contributions.
export const futureValue = (current, monthly, annualReturnPct, months) => {
  const r = annualReturnPct / 100 / 12;
  if (months <= 0) return current;
  const grown = current * Math.pow(1 + r, months);
  const contrib = r === 0 ? monthly * months : monthly * ((Math.pow(1 + r, months) - 1) / r);
  return grown + contrib;
};

// Monthly contribution required to reach `target` by `months`, given current + return.
export const requiredMonthly = (current, target, annualReturnPct, months) => {
  if (months <= 0) return Math.max(0, target - current);
  const r = annualReturnPct / 100 / 12;
  const fvCurrent = current * Math.pow(1 + r, months);
  const remaining = target - fvCurrent;
  if (remaining <= 0) return 0;
  return r === 0 ? remaining / months : (remaining * r) / (Math.pow(1 + r, months) - 1);
};

// Heuristic probability (0-100) that the plan reaches the target.
export const successProbability = (projected, target, current) => {
  if (target <= 0) return 100;
  const coverage = projected / target;
  const alreadySaved = Math.min(current / target, 1);
  const p = coverage * 82 + alreadySaved * 13;
  return Math.max(3, Math.min(99, Math.round(p)));
};

// Proprietary 0-100 Goal Health Score: blend of progress, savings adequacy, probability.
export const healthScore = ({ current, target, plannedMonthly, requiredMo, probability }) => {
  const progress = target > 0 ? Math.min(current / target, 1) : 1;
  const adequacy = requiredMo <= 0 ? 1 : Math.min(plannedMonthly / requiredMo, 1);
  const prob = (probability || 0) / 100;
  return Math.round(progress * 30 + adequacy * 35 + prob * 35);
};

// Single call that returns everything the UI needs for a goal.
export const analyzeGoal = (goal, currentOverride = null) => {
  const target = parseFloat(goal.target_amount) || 0;
  const current = currentOverride != null ? currentOverride : parseFloat(goal.current_amount) || 0;
  const annualReturn = goal.expected_return != null ? parseFloat(goal.expected_return) : 6;
  const monthly = parseFloat(goal.monthly_contribution) || 0;
  const months = monthsUntil(goal.target_date);

  const progressPct = target > 0 ? Math.min((current / target) * 100, 100) : 0;
  const gap = Math.max(0, target - current);
  const projected = futureValue(current, monthly, annualReturn, months);
  const requiredMo = requiredMonthly(current, target, annualReturn, months);
  const probability = successProbability(projected, target, current);
  const health = healthScore({ current, target, plannedMonthly: monthly, requiredMo, probability });

  let status = 'off'; // off | attention | on
  if (probability >= 75) status = 'on';
  else if (probability >= 50) status = 'attention';

  return {
    target, current, annualReturn, monthly, months,
    progressPct, gap, projected, requiredMo, probability, health, status,
  };
};

export const statusMeta = {
  on: { label: 'On Track', color: 'text-success-600', bg: 'bg-success-100', ring: '#16a34a' },
  attention: { label: 'Needs Attention', color: 'text-warning-600', bg: 'bg-warning-100', ring: '#d97706' },
  off: { label: 'Off Track', color: 'text-danger-600', bg: 'bg-danger-100', ring: '#dc2626' },
};

// Milestones at 25/50/75/100% of target.
export const buildMilestones = (target, current) =>
  [0.25, 0.5, 0.75, 1].map((p) => {
    const amount = target * p;
    return {
      pct: p * 100,
      amount,
      achieved: current >= amount && amount > 0,
      inProgress: current < amount && current >= target * (p - 0.25),
    };
  });

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
